import { combineLatest, defer, Observable, ReplaySubject, scan, Subject, switchMap } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';

import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { AdPlatformType, CustomerSummary } from '@app/core/models/common.models';
import {
  CustomerFeature,
  CustomerFeatureStatus,
  CustomerSubscription,
  InvoiceCurrency,
} from '@app/core/models/customer.models';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { RouterPath } from '@app/core/store/states/router-nav/router-nav-state.model';
import { assertArray, isArrayShallowEqual } from '@app/core/utilities/array.utilities';
import { shareRef } from '@app/shared/operators/share-ref.operator';
import { Store } from '@ngxs/store';
import { NzModalRef } from 'ng-zorro-antd/modal';

interface SubscriptionDowngradeStepState {
  link: RouterPath;
  key: CustomerFeature;
  label?: string;
  platforms?: AdPlatformType[];
  disabled?: boolean;
  active?: boolean;
  price?: number;
}

@Component({
  standalone: false,
  selector: 'adspt-subscription-downgrade-dialog',
  templateUrl: 'subscription-downgrade-dialog.component.html',
  styleUrls: ['subscription-downgrade-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubscriptionDowngradeDialogComponent {
  readonly summary$: Observable<CustomerSummary> = this.store.select(CustomerState.summary);

  readonly isAgency$: Observable<boolean> = this.store.select(CustomerState.customerIsAgency);

  @Input()
  currentPlan?: CustomerSubscription;

  @Input()
  newPlan?: CustomerSubscription;

  @Input()
  analyticsPrice?: number;

  @Input()
  currency?: InvoiceCurrency;

  @Input()
  set downgradeSteps(downgradeSteps: CustomerFeatureStatus[]) {
    this.downgradeSteps$.next(downgradeSteps);
  }

  readonly trackByKey = (_: number, step: SubscriptionDowngradeStepState) =>
    step.key + (step.platforms ? step.platforms.join('-') : '');

  readonly downgradeSteps$ = new ReplaySubject<CustomerFeatureStatus[]>(1);

  readonly updateStep$ = new Subject<Partial<SubscriptionDowngradeStepState>>();

  readonly steps: SubscriptionDowngradeStepState[] = [
    { link: RouterPath.CriterionAdditionRulesAdvancedMode, key: CustomerFeature.CriterionAdditionAdvanced },
    { link: RouterPath.Goals, key: CustomerFeature.SecondGoalForPG },
    { link: RouterPath.Goals, key: CustomerFeature.MinBid },
    { link: RouterPath.Goals, key: CustomerFeature.ProfitOptimization },
    {
      link: RouterPath.CustomerAccountSettings,
      key: CustomerFeature.SearchOptimization,
      platforms: [AdPlatformType.Google],
    },
    {
      link: RouterPath.CustomerAccountSettings,
      key: CustomerFeature.SearchOptimization,
      platforms: [AdPlatformType.Microsoft],
    },
    { link: RouterPath.CustomerIntegrationSettings, key: CustomerFeature.MerchantSpring },
  ];

  readonly steps$ = defer(() =>
    combineLatest([
      this.downgradeSteps$,
      this.summary$.pipe(map((summary) => assertArray(summary?.accountPlatforms))),
    ]).pipe(
      map(([downgradeSteps, platforms]) =>
        this.steps.reduce((steps, step) => {
          const downgradeStep = downgradeSteps.find((s) => s.name === step.key);
          const hasPlatforms = step.platforms?.some((platform) => platforms.includes(platform as any));

          if (!downgradeStep.available || step.key === CustomerFeature.MerchantSpring) {
            steps.push({
              ...step,
              label: `${step.key}${step.platforms ? `-${step.platforms.join('-')}` : ''}`,
              disabled: !downgradeStep.inUse && !hasPlatforms,
              active: !downgradeStep.inUse && !hasPlatforms,
              price: step.key === CustomerFeature.MerchantSpring ? this.analyticsPrice : 0,
            });
          }

          return steps;
        }, []),
      ),
      switchMap((steps) =>
        this.updateStep$.pipe(
          scan(
            (steps, update) =>
              steps.map((s) =>
                s.key === update.key && isArrayShallowEqual(assertArray(s.platforms), assertArray(update.platforms))
                  ? { ...s, active: update.active }
                  : s,
              ),
            steps,
          ),
          startWith(steps),
        ),
      ),
      shareRef(),
    ),
  );

  readonly isDowngradePossible$ = this.steps$.pipe(map((steps) => steps.every((step) => step.active)));

  constructor(
    private mr: NzModalRef,
    private store: Store,
  ) {
    this.isDowngradePossible$.pipe(distinctUntilChanged()).subscribe((isDowngradePossible) => {
      this.mr.updateConfig({ nzOkDisabled: !isDowngradePossible });
    });
  }
}
